import React from 'react';
import { Modal, ScrollView, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import CustomButton from '../../../components/CustomButton';
import { removeDeletedDateFromSerialNo } from '../../../utils/removeDeletedDateFromSerialNo';

interface Props {
    isVisible: boolean;
    setIsVisible: React.Dispatch<React.SetStateAction<boolean>>;
    cameraHistory?: CameraHistoryPage;
}

function SideBarItem(props: { title: string; content: string; direction: 'row' | 'column'; contentTextColor?: string; }) {
    const { styles } = useStyles(stylesheet);

    return <View style={{ flexDirection: props.direction, gap: props.direction === 'row' ? 8 : 0 }}>
        <MyAppText style={styles.sideBarItemTitle}>{props.title}:</MyAppText>
        <MyAppText style={[styles.sideBarItemContent, props.contentTextColor ? { color: props.contentTextColor } : undefined]}>{props.content}</MyAppText>
    </View>;
}

export default function CameraInformationModal({ isVisible, setIsVisible, cameraHistory }: Props) {
    const { styles, theme } = useStyles(stylesheet);

    if (!cameraHistory) {
        return <></>;
    }

    return (
        <Modal
            visible={isVisible}
            animationType='fade'
            transparent
            onRequestClose={() => {
                setIsVisible(false);
            }}
        >
            <View style={styles.modalContainer}>
                <View style={styles.modalComponent}>
                    <MyAppText style={styles.modalTitle}>{translate('cameraInfoLabel')}</MyAppText>
                    <View style={styles.fieldsView}>
                        <ScrollView contentContainerStyle={[styles.fieldsView, { maxHeight: 400 }]}>
                            <View style={{ gap: 4 }}>
                                <SideBarItem title={translate('Id')} content={cameraHistory.camera.id} direction='row' />
                                <SideBarItem title={translate('title')} content={cameraHistory.camera.title} direction='column' />
                                <SideBarItem title={translate('serialNo')} content={removeDeletedDateFromSerialNo(cameraHistory.camera.serialNo ?? '')} direction='row' />
                                <SideBarItem title={translate('address')} content={cameraHistory.camera.address} direction='column' />
                                <SideBarItem title={translate('stolen_equipment')} content={cameraHistory.camera.stolen ? translate('yes') : translate('no')} direction='row' />
                                <SideBarItem
                                    title={translate('currentlyOnline')}
                                    contentTextColor={cameraHistory.camera.isConnected ? theme.colors.online.fontColor : theme.colors.offline.fontColor}
                                    content={cameraHistory.camera.isConnected ? translate('online') : translate('offline')} direction='row'
                                />
                                <SideBarItem title={translate('disabled')} content={cameraHistory.camera.disabledAt !== null ? translate('yes') : translate('no')} direction='row' />
                                <SideBarItem title={translate('femaleDeleted')} content={cameraHistory.camera.deletedAt !== null ? translate('yes') : translate('no')} direction='row' />
                            </View>
                        </ScrollView>
                        <CustomButton
                            text={translate('close')}
                            type='secondary'
                            onPress={() => {
                                setIsVisible(false);
                            }}
                        />
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const stylesheet = createStyleSheet(theme => ({
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199',
        justifyContent: 'center',
        alignItems: 'center',
    },

    fieldsView: {
        gap: 15,
    },

    modalComponent: {
        minWidth: {
            xs: undefined,
            md: 450
        },
        minHeight: 150,
        width: {
            md: undefined,
            xs: '90%'
        },
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    },

    modalTitle: {
        fontSize: 18,
        fontWeight: '600'
    },
    sideBarItemTitle: {
        fontSize: 14,
        color: theme.colors.neutral[500],
        fontWeight: '600',
    },
    sideBarItemContent: {
        fontSize: 14,
        color: theme.colors.neutral[400],
    },
}));
